import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { ReactComponent as DownloadAreaSvg } from '../../../assets/icons/dowload_area.svg';
import { useFileList } from '../../../hooks/use-file-list';
import { IAppFile } from '../../../types/interfaces/app-file';
import { INewFile } from '../../../types/interfaces/new-file';
import AppFile from '../app-file/AppFile';
import { useStyles } from './AppFileContainer.styles';

interface AppFilesProps {
  files: IAppFile[];
  onFilesToDeleteChanged?: (ids: string[]) => void;
  onFilesToUploadChanged?: (files: INewFile[]) => void;
  onMainFileChanged?: (id: string | number) => void;
  editMode?: boolean;
  mainFileLabel?: string;
  mainFileId?: string;
  uploadPreview?: boolean;
}

export default function AppFileContainer(props: AppFilesProps) {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();

  const {
    files,
    editMode = false,
    onFilesToDeleteChanged,
    onFilesToUploadChanged,
    onMainFileChanged,
    mainFileLabel = t('common.avatar'),
    mainFileId,
    uploadPreview = false,
  } = props;

  const {
    fileIdsToDelete,
    filesToUpload,
    resultFiles,
    onDeleteFile,
    onUploadFiles,
  } = useFileList(files || []);

  useEffect(() => {
    if (onFilesToDeleteChanged) onFilesToDeleteChanged(fileIdsToDelete);
  }, [fileIdsToDelete]);

  useEffect(() => {
    if (onFilesToUploadChanged) onFilesToUploadChanged(filesToUpload);

    if (filesToUpload?.length && !mainFileId && onMainFileChanged)
      onMainFileChanged(filesToUpload[0]?.id);
  }, [filesToUpload]);

  return (
    <Box className={classes.filesContainerPageWrapper}>
      <Box className={classes.filesWrapper}>
        {editMode && (
          <Box component="label" className={classes.uploadTrigger}>
            <input
              autoComplete="off"
              className={classes.fileInputOverlay}
              multiple
              tabIndex={-1}
              type="file"
              accept="image/*"
              onChange={async (e) => {
                await onUploadFiles(e.target.files, uploadPreview);
                e.target.value = '';
              }}
            />
            <DownloadAreaSvg className={classes.uploadFileLabel} />
          </Box>
        )}
        {resultFiles?.map((file) => (
          <Box
            key={file.id}
            className={clsx(
              classes.file,
              file.id === mainFileId && classes.mainFile,
            )}>
            <AppFile
              key={file?.id}
              file={file}
              onDelete={
                editMode &&
                ((file: IAppFile | INewFile) => onDeleteFile(file.id))
              }
              onClick={() => {
                if (editMode && onMainFileChanged) {
                  onMainFileChanged(file?.id);
                }
              }}
            />
            {mainFileId === file.id && (
              <Box className={classes.mainFileLabel}>{mainFileLabel}</Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
